'use client';
import { isBodyScrollLocked } from '@/lib/bodyScrollLock';

import { useCallback, useEffect, useRef, useState } from 'react';
import { AxisPlane, navPlaneHeadingForScrollDelta } from './AxisPlane';
import styles from './cockpit.module.css';

const IDLE_MS = 2600;

function scrollMetrics() {
  const el = document.documentElement;
  const max = Math.max(0, el.scrollHeight - el.clientHeight);
  const top = window.scrollY || el.scrollTop || 0;
  return { max, top, progress: max > 0 ? top / max : 0 };
}

/**
 * Page-level runway: the native scrollbar is hidden; this overlay is the
 * track. A logo-blue plane taxis with scroll and points toward remaining
 * page (nose down at top, nose up at bottom). Idle: slides off to the right.
 */
export function RunwayNavScrollbar() {
  const [progress, setProgress] = useState(0);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [desktop, setDesktop] = useState(false);
  const [inUse, setInUse] = useState(true);
  const [heading, setHeading] = useState(90);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pinned = useRef(false);
  const lastScrollTop = useRef<number | null>(null);

  const bump = useCallback(() => {
    if (pinned.current) {
      setInUse(true);
      return;
    }
    setInUse(true);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setInUse(false), IDLE_MS);
  }, []);

  const sync = useCallback(() => {
    if (isBodyScrollLocked()) return; // a pinned <body> reads max=0 (Opus 2026-09-28)
    const { max, progress: next, top } = scrollMetrics();
    setHasOverflow(max > 8);
    setProgress(next);
    if (lastScrollTop.current !== null) {
      const delta = top - lastScrollTop.current;
      setHeading((current) => navPlaneHeadingForScrollDelta(current, delta));
    }
    lastScrollTop.current = top;
    bump();
  }, [bump]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onMq = () => setDesktop(mq.matches);
    onMq();
    mq.addEventListener('change', onMq);
    return () => mq.removeEventListener('change', onMq);
  }, []);

  useEffect(() => {
    if (!desktop) return;
    sync();
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    const onMove = (e: MouseEvent) => {
      if (e.clientX >= window.innerWidth - 40) bump();
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    const ro = new ResizeObserver(sync);
    ro.observe(document.documentElement);
    if (document.body) ro.observe(document.body);
    return () => {
      window.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
      window.removeEventListener('mousemove', onMove);
      ro.disconnect();
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [sync, bump, desktop]);

  const seek = (clientY: number, target: HTMLElement) => {
    const { max } = scrollMetrics();
    if (max <= 0) return;
    const rect = target.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
    window.scrollTo({ top: pct * max, behavior: 'auto' });
  };

  if (!desktop || !hasOverflow) return null;

  const travel = `calc(23px + ${progress} * (100% - 46px))`;

  return (
    <div
      role="scrollbar"
      aria-orientation="vertical"
      aria-label="Page position on the runway"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress * 100)}
      tabIndex={0}
      className={`${styles.navRunway} ${inUse ? '' : styles.navRunwayIdle}`.trim()}
      onClick={(e) => seek(e.clientY, e.currentTarget)}
      onMouseEnter={() => {
        pinned.current = true;
        setInUse(true);
        if (idleTimer.current) clearTimeout(idleTimer.current);
      }}
      onMouseLeave={() => {
        pinned.current = false;
        bump();
      }}
      onFocus={() => {
        pinned.current = true;
        setInUse(true);
      }}
      onBlur={() => {
        pinned.current = false;
        bump();
      }}
      onKeyDown={(e) => {
        const { max, top } = scrollMetrics();
        if (max <= 0) return;
        const page = window.innerHeight * 0.9;
        if (e.key === 'ArrowDown' || e.key === 'PageDown') {
          e.preventDefault();
          window.scrollTo({ top: Math.min(max, top + (e.key === 'PageDown' ? page : 80)) });
        } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
          e.preventDefault();
          window.scrollTo({ top: Math.max(0, top - (e.key === 'PageUp' ? page : 80)) });
        } else if (e.key === 'Home') {
          e.preventDefault();
          window.scrollTo({ top: 0 });
        } else if (e.key === 'End') {
          e.preventDefault();
          window.scrollTo({ top: max });
        }
      }}
    >
      <AxisPlane
        className={styles.navPlane}
        style={{
          top: travel,
          transform: `translate(-50%, -50%) rotate(${heading}deg)`,
        }}
      />
    </div>
  );
}
